/**
 * A reusable confirmation — a bottom sheet on mobile, a centred full-bleed band
 * on desktop (no card, D-1). It reuses the app's one modal idiom (the bottom
 * sheet from `AddCategorySheet`/`EditExpenseSheet`) and adds the `md:` centring.
 *
 * The actions are passed in so one component serves every confirm: a single or
 * bulk category delete with the expense-handling choice, a plain delete, and the
 * Ledger's expense delete. This is a place where the user's money is at stake, so
 * the copy stays plain (D-7) — no myth. The destructive default is coral and
 * listed first (INV-7 — the safe, explicit choice is the prominent one); Cancel
 * is always last.
 *
 * Escape, backdrop, handle and downward drag all animate out then cancel.
 */

import type { ReactNode } from "react";

import { Sheet } from "@/features/common/sheet";
import { cx } from "@/features/common/primitives";

export interface ConfirmAction {
  label: ReactNode;
  onSelect: () => void;
  tone?: "danger" | "neutral";
  disabled?: boolean;
}

export function ConfirmSheet({
  title,
  body,
  actions,
  onCancel,
  cancelLabel = "Cancel",
  openHaptic = true,
}: {
  title: string;
  body?: ReactNode;
  actions: ConfirmAction[];
  onCancel: () => void;
  cancelLabel?: string;
  openHaptic?: boolean;
}): React.JSX.Element {
  return (
    <Sheet openHaptic={openHaptic} title={title} onClose={onCancel}>
      {(close) => (
        <div className="flex flex-col gap-phi-0 px-phi-1">
          {body ? <p className="text-phi-0 text-tx-2">{body}</p> : null}
          <div className="flex flex-col gap-phi-n3">
            {actions.map((action, index) => (
              <button
                key={`${action.tone ?? "neutral"}-${String(index)}`}
                type="button"
                disabled={action.disabled}
                onClick={action.onSelect}
                className={cx(
                  "flex min-h-phi-5 items-center justify-center rounded-lg px-phi-0 text-phi-n1 font-semibold disabled:opacity-35",
                  action.tone === "danger"
                    ? "bg-over text-on-over"
                    : "border border-line bg-bg-2 text-tx",
                )}
              >
                {action.label}
              </button>
            ))}
            <button
              type="button"
              onClick={close}
              className="min-h-[44px] text-phi-n1 font-medium text-tx-2"
            >
              {cancelLabel}
            </button>
          </div>
        </div>
      )}
    </Sheet>
  );
}
